import React, { createContext, useEffect, useState, useRef, useCallback } from "react";
import authService from "../utils/authService";
import { supabase } from "../utils/supabase";
import profileDeleteService from "../utils/profileDeleteService";
import loginSessionService from "../utils/loginSessionService";

// Define a global state to survive hot reloads
// This fixes issues with Vite HMR refreshing the component multiple times
if (!window.VA_SPROUTS_AUTH_STATE) {
  window.VA_SPROUTS_AUTH_STATE = {
    initialized: false,
    user: null,
    userProfile: null,
    lastChecked: 0,
  };
}

const globalAuthState = window.VA_SPROUTS_AUTH_STATE;

const AuthContext = createContext();

// Only try to initialize auth once every 5 seconds max
const AUTH_COOLDOWN = 5000;

export function AuthProvider({ children }) {
  // State management with recovery from sessionStorage
  const [user, setUser] = useState(() => {
    // Try to recover from globalAuthState first (survives HMR)
    if (globalAuthState.user) {
      return globalAuthState.user;
    }
    
    // Then try sessionStorage
    try {
      const savedUser = sessionStorage.getItem('authUser');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('Error restoring user from sessionStorage:', e);
      return null;
    }
  });
  
  const [userProfile, setUserProfile] = useState(() => {
    // Try to recover from globalAuthState first
    if (globalAuthState.userProfile) {
      return globalAuthState.userProfile;
    }
    
    // Then try sessionStorage
    try {
      const savedProfile = sessionStorage.getItem('authUserProfile');
      return savedProfile ? JSON.parse(savedProfile) : null;
    } catch (e) {
      console.error('Error restoring userProfile from sessionStorage:', e);
      return null;
    }
  });
  
  // Start with loading false if we already have auth state
  const [loading, setLoading] = useState(!user || !userProfile);
  const [authError, setAuthError] = useState(null);
  
  // Refs for tracking initialization
  const authInitializing = useRef(false);
  
  // Safe setter functions to update both state and global state
  const updateUser = useCallback((newUser) => {
    setUser(newUser);
    globalAuthState.user = newUser;
    
    try {
      if (newUser) {
        sessionStorage.setItem('authUser', JSON.stringify(newUser));
      } else {
        sessionStorage.removeItem('authUser');
      }
    } catch (e) {
      console.error('Failed to update user in sessionStorage', e);
    }
  }, []);
  
  const updateUserProfile = useCallback((newProfile) => {
    setUserProfile(newProfile);
    globalAuthState.userProfile = newProfile;
    
    try {
      if (newProfile) {
        sessionStorage.setItem('authUserProfile', JSON.stringify(newProfile));
      } else {
        sessionStorage.removeItem('authUserProfile');
      }
    } catch (e) {
      console.error('Failed to update userProfile in sessionStorage', e);
    }
  }, []);
  
  // Initialize auth with direct Supabase session check and proper error handling
  const initializeAuth = useCallback(async () => {
    // Prevent multiple initialization attempts running in parallel
    if (authInitializing.current) return;
    authInitializing.current = true;
    
    // Throttle initialization attempts
    const now = Date.now();
    if (now - globalAuthState.lastChecked < AUTH_COOLDOWN) {
      authInitializing.current = false;
      return;
    }
    
    globalAuthState.lastChecked = now;

    try {
      setLoading(true);
      setAuthError(null);
      
      // First check Supabase connectivity
      if (process.env.NODE_ENV === 'development') {
        console.log('Checking Supabase connection before auth initialization...');
      }
      const connectionCheck = await authService.testConnection();
      if (!connectionCheck.connected) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase connection failed:', connectionCheck.error);
        }
        setAuthError(connectionCheck.error || 'Unable to connect to authentication service');
        setLoading(false);
        authInitializing.current = false;
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Supabase connection successful, proceeding with auth initialization');
      }
      
      // Direct session check with supabase to avoid possible issues
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error getting auth session:', error.message);
        }
        setAuthError(error.message);
        setLoading(false);
        authInitializing.current = false;
        return;
      }
      
      if (!data.session) {
        // No active session
        updateUser(null);
        updateUserProfile(null);
        setLoading(false);
        authInitializing.current = false;
        return;
      }
      
      // We have a valid session
      const authUser = data.session.user;
      updateUser(authUser);
      
      // Fetch user profile
      const profileResult = await authService.getUserProfile(authUser.id);
      
      if (profileResult?.success) {
        if (profileResult.data) {
        // Check if user is verified before allowing them to be signed in
        if (!profileResult.data.is_verified) {
          // User has a session but is not verified - check if this is during verification process
          if (process.env.NODE_ENV === 'development') {
            console.log('User session exists but email not verified');
          }

          // Check if the user's email has been confirmed at the auth level recently (within last 5 minutes)
          const emailConfirmedAt = authUser.email_confirmed_at;
          const isRecentlyConfirmed = emailConfirmedAt &&
            (new Date() - new Date(emailConfirmedAt)) < 5 * 60 * 1000; // 5 minutes

          if (isRecentlyConfirmed) {
            if (process.env.NODE_ENV === 'development') {
              console.log('User recently confirmed at auth level, updating profile verification');
            }
            try {
              await supabase
                .from('user_profiles')
                .update({ 
                  is_verified: true,
                  updated_at: new Date().toISOString() 
                })
                .eq('id', authUser.id);
              
              // Refetch the profile with updated verification status
              const updatedProfileResult = await authService.getUserProfile(authUser.id);
              if (updatedProfileResult?.success && updatedProfileResult.data) {
                updateUserProfile(updatedProfileResult.data);
              }
            } catch (profileUpdateError) {
              console.error('Failed to update profile verification during auth init:', profileUpdateError);
              // Continue with unverified state
            }
          } else {
            // Force sign out to prevent unverified access (but only if not during verification)
            if (process.env.NODE_ENV === 'development') {
              console.log('Signing out unverified user');
            }
            
            try {
              await supabase.auth.signOut();
            } catch (signOutError) {
              console.error('Error signing out unverified user:', signOutError);
              // Try global sign out as fallback
              try {
                await supabase.auth.signOut({ scope: 'global' });
              } catch (globalSignOutError) {
                console.error('Global sign out failed:', globalSignOutError);
              }
            }
            
            updateUser(null);
            updateUserProfile(null);
            setAuthError('Please verify your email address before accessing your account. Check your email for a verification link.');
            setLoading(false);
            authInitializing.current = false;
            return;
          }
        } else {
          updateUserProfile(profileResult.data);
        }
        } else {
          // Create profile if it doesn't exist - with automatic public visibility
          if (process.env.NODE_ENV === 'development') {
            console.log('No profile found for user, creating one automatically');
          }

          const usernameBase = authUser.email?.split('@')[0] || 'user';
          const timestamp = Date.now().toString().slice(-6);
          const autoUsername = `${usernameBase}-${timestamp}`;

          const profileData = {
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name ||
                      authUser.email?.split('@')[0] || '',
            role: authUser.user_metadata?.role || 'va',
            is_public_profile: true,
            public_username: autoUsername
          };

          if (process.env.NODE_ENV === 'development') {
            console.log('Creating profile with data:', JSON.stringify(profileData));
          }

          const createResult = await authService.createUserProfile(authUser.id, profileData);

          if (process.env.NODE_ENV === 'development') {
            console.log('Profile creation result:', {
              success: createResult?.success,
              error: createResult?.error,
              errorType: createResult?.type,
              profileData: createResult?.data ? 'data exists' : 'no data'
            });
          }
          
          if (createResult?.success) {
            updateUserProfile(createResult.data);
          } else if (createResult?.error && createResult?.type === 'auth_error_401') {
            // Special handling for 401 errors during profile creation
            console.error('401 Unauthorized error during profile creation in auth context');

            // Force a reinitialization of the auth context by signing out and in again
            try {
              if (process.env.NODE_ENV === 'development') {
                console.log('Attempting to refresh auth due to 401 error');
              }
              await supabase.auth.refreshSession();
              
              // Retry profile creation after refresh
              const retryResult = await authService.createUserProfile(authUser.id, profileData);
              if (retryResult?.success) {
                updateUserProfile(retryResult.data);
              } else {
                setAuthError('Failed to create user profile after session refresh. Please log out and back in.');
              }
            } catch (refreshError) {
              console.error('Failed to refresh session:', refreshError);
              setAuthError('Authentication error. Please log out and back in.');
            }
          }
        }
      } else if (profileResult?.error) {
        console.error('Error fetching user profile:', profileResult.error);
        setAuthError(profileResult.error);
      }
    } catch {
      // Catch any initialization errors
      setAuthError('Failed to initialize authentication');
    } finally {
      setLoading(false);
      authInitializing.current = false;
      globalAuthState.initialized = true;
    }
  }, [updateUser, updateUserProfile]);
  
  // Set a maximum timeout for auth loading
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 10000);
      
      return () => clearTimeout(timeout);
    }
  }, [loading]);
  
  // Main initialization effect - runs once on component mount
  useEffect(() => {
    // Check if we already have a valid auth state and skip initialization
    if (globalAuthState.initialized && user && userProfile) {
      return;
    }
    // Don't re-initialize if user is null and we just completed a sign-out
    if (globalAuthState.initialized && !user && !userProfile) {
      return;
    }
    initializeAuth();
    
    // Cleanup function
    return () => {
      // No cleanup needed for initialization
    };
  }, [initializeAuth, user, userProfile]);
  
  // Listen for Supabase auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Throttle auth state change responses to prevent loops
      // IMPORTANT: Never throttle SIGNED_OUT events - they must always be processed
      const now = Date.now();
      if (event !== 'SIGNED_OUT' && now - globalAuthState.lastChecked < AUTH_COOLDOWN / 2) {
        return;
      }
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Check if user is verified before allowing automatic sign-in
        if (session?.user) {
          try {
            const profileResult = await authService.getUserProfile(session.user.id);
            if (profileResult?.success && profileResult.data && !profileResult.data.is_verified) {
              // Check if the user's email has been confirmed at the auth level
              const emailConfirmedAt = session.user.email_confirmed_at;

              if (emailConfirmedAt) {
                // Email is confirmed at Supabase level - sync the profile to verified
                if (process.env.NODE_ENV === 'development') {
                  console.log('User email confirmed at auth level during state change, updating profile verification');
                }
                try {
                  await supabase
                    .from('user_profiles')
                    .update({
                      is_verified: true,
                      updated_at: new Date().toISOString()
                    })
                    .eq('id', session.user.id);
                  if (process.env.NODE_ENV === 'development') {
                    console.log('Profile verification status synced successfully');
                  }
                } catch (updateError) {
                  console.error('Failed to update profile verification during auth state change:', updateError);
                }
              } else {
                // User exists but email is not confirmed at Supabase level - sign them out
                if (process.env.NODE_ENV === 'development') {
                  console.log('Preventing auto-sign-in for unverified user (email not confirmed at Supabase level)');
                }
                await supabase.auth.signOut();
                return;
              }
            }
          } catch (verificationError) {
            console.error('Error checking verification status during auth state change:', verificationError);
            // Continue with normal flow if we can't check verification
          }
        }
        
        // Only re-initialize if we don't already have matching state
        if (!user || (session?.user?.id && user?.id !== session?.user?.id)) {
          initializeAuth();
        }
      } else if (event === 'SIGNED_OUT') {
        // Clear auth state on sign out - always process immediately
        updateUser(null);
        updateUserProfile(null);
        globalAuthState.initialized = false;
        setLoading(false);
        
        // Broadcast logout to other tabs via localStorage
        try {
          localStorage.setItem('va-sprouts-logout', Date.now().toString());
        } catch (e) {
          // localStorage might not be available
        }
      }
    });
    
    // Cleanup function
    return () => {
      subscription?.unsubscribe();
    };
  }, [initializeAuth, user, updateUser, updateUserProfile]);
  
  // Cross-tab logout synchronization via localStorage events
  useEffect(() => {
    const handleStorageEvent = (e) => {
      if (e.key === 'va-sprouts-logout' && e.newValue) {
        // Another tab logged out - clear auth state in this tab too
        if (process.env.NODE_ENV === 'development') {
          console.log('Cross-tab logout detected, clearing auth state');
        }
        updateUser(null);
        updateUserProfile(null);
        globalAuthState.initialized = false;
        setLoading(false);
      }
    };
    
    window.addEventListener('storage', handleStorageEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [updateUser, updateUserProfile]);
  
  // Clear error function
  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);
  
  // Sign in function
  const signIn = useCallback(async (email, password) => {
    try {
      setLoading(true);
      clearError();
      
      // First check Supabase connectivity
      if (process.env.NODE_ENV === 'development') {
        console.log('Checking Supabase connection before sign in...');
      }
      const connectionCheck = await authService.testConnection();
      if (!connectionCheck.connected) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase connection failed during sign in:', connectionCheck.error);
        }
        setAuthError(connectionCheck.error || 'Unable to connect to authentication service. Please check your internet connection and try again.');
        return { 
          success: false, 
          error: connectionCheck.error || 'Unable to connect to authentication service. Please check your internet connection and try again.',
          type: 'connection_error'
        };
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Supabase connection successful, proceeding with sign in');
      }
      const result = await authService.signIn(email, password);
      
      if (result?.success && result?.data?.user) {
        updateUser(result.data.user);
        
        // Fetch user profile after successful login
        const profileResult = await authService.getUserProfile(result.data.user.id);
        
        if (profileResult?.success && profileResult?.data) {
          updateUserProfile(profileResult.data);
          
          // Track login session for security monitoring
          try {
            await loginSessionService.trackLogin();
            
            // Clean up any old login notifications (notifications disabled per user feedback)
            loginSessionService.cleanupLoginNotifications(result.data.user.id).catch(err => {
              // Non-critical, just log if it fails
              if (process.env.NODE_ENV === 'development') {
                console.log('Login notification cleanup skipped:', err);
              }
            });
            
            // Setup session timeout if enabled in user's security settings
            const securitySettings = profileResult.data.security_settings;
            if (securitySettings?.session_timeout && securitySettings.session_timeout > 0) {
              loginSessionService.setupSessionTimeout(securitySettings.session_timeout);
            }
          } catch (sessionError) {
            console.error('Error tracking login session:', sessionError);
            // Don't fail the login if session tracking fails
          }
        }
        
        // Check if user needs email verification
        if (result.needsVerification) {
          if (process.env.NODE_ENV === 'development') {
            console.log('User logged in but needs email verification:', result.userEmail);
          }
          // Set a verification reminder flag that can be used by components
          // The UI can show a notification or banner prompting verification
        }
        
        return { 
          success: true, 
          data: result.data,
          needsVerification: result.needsVerification,
          userEmail: result.userEmail
        };
      } else {
        // Handle login failure
        console.error('Sign in failed:', result?.error);
        setAuthError(result?.error || 'Login failed');
        return result; // Return the error result from authService
      }
    } catch {
      // Catch any sign in errors
      const errorMessage = 'An unexpected error occurred during sign in';
      console.error(errorMessage);
      setAuthError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        type: 'unexpected_error'
      };
    } finally {
      setLoading(false);
    }
  }, [updateUser, updateUserProfile, clearError]);

  // Sign out function
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      
      // Clean up session timeout
      try {
        loginSessionService.clearSessionTimeout();
      } catch (sessionError) {
        console.error('Error cleaning up session:', sessionError);
        // Don't fail the logout if session cleanup fails
      }
      
      const result = await authService.signOut();
      if (result?.success) {
        updateUser(null);
        updateUserProfile(null);
        globalAuthState.initialized = false;
        
        // Clear any stored auth data
        sessionStorage.removeItem('authUser');
        sessionStorage.removeItem('authUserProfile');
        
        // Broadcast logout to other tabs via localStorage
        try {
          localStorage.setItem('va-sprouts-logout', Date.now().toString());
        } catch (e) {
          // localStorage might not be available
        }
      } else if (result?.error) {
        setAuthError(result.error);
      }
    } catch {
      // Catch any sign out errors
      setAuthError('Failed to sign out');
    } finally {
      setLoading(false);
    }
  }, [updateUser, updateUserProfile]);

  // Sign up function
  const signUp = useCallback(async (email, password, userData = {}) => {
    try {
      setLoading(true);
      clearError();
      
      // Check if email is already registered - only for internal use
      const emailCheckResult = await authService.checkEmailExists(email);
      
      // Only block registration if we're certain the email exists
      if (emailCheckResult?.exists && !emailCheckResult?.uncertain) {
        setAuthError('An account with this email already exists. Please sign in instead.');
        return { 
          success: false, 
          error: 'An account with this email already exists. Please sign in instead.',
          type: 'user_exists'
        };
      } else if (emailCheckResult?.uncertain) {
        // If we're uncertain about the email existence, log it but continue with registration
        if (process.env.NODE_ENV === 'development') {
          console.log('Email existence check was uncertain, proceeding with registration attempt');
        }
      }
      
      // Check session state before signup
      const { data: sessionCheck } = await supabase.auth.getSession();
      if (sessionCheck?.session) {
        // Sign out first to avoid conflicts
        await supabase.auth.signOut();
      }
      
      const result = await authService.signUp(email, password, userData);
      
      if (result?.success) {
        // Email verification required - check email sent
        if (result?.data?.user && !result?.data?.session) {
          // User created but session not activated - email verification needed
        }
        
        // Don't automatically sign in after signup since email verification is required
        return { success: true, data: result.data };
      } else {
        // Handle specific error cases
        if (result?.type === 'auth_error' || result?.type === 'auth_error_401') {
          console.error('Authentication error during signup');

          // Check if this is a 401 error
          if (result.error.includes('401') || result.error.includes('unauthorized')) {
            // Try refreshing the auth session
            try {
              if (process.env.NODE_ENV === 'development') {
                console.log('Attempting to refresh auth due to 401 error');
              }
              await supabase.auth.refreshSession();
              
              // Retry signup after refresh
              const retryResult = await authService.signUp(email, password, userData);
              if (retryResult?.success) {
                return { success: true, data: retryResult.data };
              } else {
                const errorMessage = 'Registration failed after session refresh. Please try a different browser or clear your cookies.';
                setAuthError(errorMessage);
                return { 
                  success: false, 
                  error: errorMessage,
                  type: 'auth_refresh_failed'
                };
              }
            } catch (refreshError) {
              console.error('Failed to refresh session:', refreshError);
              const errorMessage = 'Authentication error during registration. Please try again in a different browser or clear your cookies.';
              setAuthError(errorMessage);
              return { 
                success: false, 
                error: errorMessage,
                type: 'auth_refresh_error'
              };
            }
          }
        }
        
        // Handle signup failure with more specific error messages
        let errorMessage = result?.error || 'Registration failed';
        
        // Check for email service specific errors
        if (result?.error?.includes('Email service not configured') || 
            result?.error?.includes('Resend API error') ||
            result?.error?.includes('Email service not available')) {
          errorMessage = 'Registration successful, but verification email could not be sent. Please contact support.';
        }
        
        setAuthError(errorMessage);
        return { 
          ...result, 
          userMessage: errorMessage 
        };
      }
    } catch (error) {
      // Catch any sign up errors
      const errorMessage = 'An unexpected error occurred during registration';
      console.error('SignUp error in AuthContext:', error);
      setAuthError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        type: 'unexpected_error'
      };
    } finally {
      setLoading(false);
    }
  }, [clearError]);
  
  // Update user profile function
  const updateProfile = useCallback(async (profileData) => {
    try {
      if (!user || !user.id) {
        const errorMessage = 'User not authenticated. Please log in again.';
        setAuthError(errorMessage);
        return { success: false, error: errorMessage };
      }
      
      // Validate that profileData is an object
      if (!profileData || typeof profileData !== 'object') {
        const errorMessage = 'Invalid profile data provided';
        setAuthError(errorMessage);
        return { success: false, error: errorMessage };
      }
      
      // Note: Do NOT set global loading state here. Profile updates are not auth
      // state changes and setting loading=true causes ProtectedRoute to unmount
      // children mid-save. Callers manage their own loading state.
      setAuthError(null);
      
      // Call the authService to update the profile
      const result = await authService.updateUserProfile(user.id, profileData);
      
      if (result?.success && result?.data) {
        // Update local state with the new profile data
        updateUserProfile(result.data);
        return { success: true, data: result.data };
      } else {
        const errorMessage = result?.error || 'Failed to update profile. Please check your connection and try again.';
        setAuthError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      // Catch any profile update errors
      const errorMessage = error?.message || 'An unexpected error occurred updating your profile';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [user, updateUserProfile]);

  // Refresh user profile function
  const refreshUserProfile = useCallback(async () => {
    try {
      if (!user || !user.id) {
        return { success: false, error: 'User not authenticated' };
      }
      
      // Note: Do NOT set global loading state here. Profile refresh is not an auth
      // state change and setting loading=true causes ProtectedRoute to unmount children.
      
      // Fetch updated user profile
      const profileResult = await authService.getUserProfile(user.id);
      
      if (profileResult?.success && profileResult?.data) {
        // Update local state with the refreshed profile data
        updateUserProfile(profileResult.data);
        return { success: true, data: profileResult.data };
      } else {
        setAuthError(profileResult?.error || 'Failed to refresh profile');
        return { success: false, error: profileResult?.error || 'Failed to refresh profile' };
      }
    } catch {
      // Catch any profile refresh errors
      const errorMessage = 'An unexpected error occurred refreshing your profile';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [user, updateUserProfile, setAuthError]);
  
  // Delete user account function
  const deleteAccount = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthContext: deleteAccount called');
      }

      if (!user || !user.id) {
        if (process.env.NODE_ENV === 'development') {
          console.log('AuthContext: No authenticated user found');
        }
        return { success: false, error: 'User not authenticated' };
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('AuthContext: Starting account deletion for user ID:', user.id);
      }
      setLoading(true);
      setAuthError(null);

      // Call the profileDeleteService to delete the account
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthContext: Calling profileDeleteService.deleteUserAccount');
      }
      const result = await profileDeleteService.deleteUserAccount(user.id);
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthContext: profileDeleteService result:', result);
      }

      if (result?.success) {
        if (process.env.NODE_ENV === 'development') {
          console.log('AuthContext: Account deletion successful, clearing user data');
        }
        // Clear all user data
        updateUser(null);
        updateUserProfile(null);

        // Force sign out regardless of whether the auth user was fully deleted
        if (process.env.NODE_ENV === 'development') {
          console.log('AuthContext: Signing out user');
        }
        await supabase.auth.signOut({ scope: 'global' }); // Use global scope to sign out from all devices
        
        // Clear local storage 
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('vasprouts.user');
        localStorage.removeItem('vasprouts.userProfile');
        
        return { success: true, message: 'Your account has been successfully deleted', warning: result.warning };
      } else {
        console.error('AuthContext: Account deletion failed:', result?.error);
        setAuthError(result?.error || 'Failed to delete account');
        return { success: false, error: result?.error || 'Failed to delete account' };
      }
    } catch (error) {
      console.error('Error in deleteAccount:', error);
      const errorMessage = 'An unexpected error occurred deleting your account';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [user, updateUser, updateUserProfile]);

  // Context value
  const contextValue = {
    user,
    userProfile,
    loading,
    authError,
    signIn,
    signUp,
    signOut,
    setAuthError,
    clearError,
    updateProfile,
    refreshUserProfile,
    deleteAccount,
    isAuthenticated: !!user,
    userProfileExists: !!userProfile,
    userRole: userProfile?.role || 'none',
    initializeAuth
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// AuthContext is exported for direct use if needed
export default AuthContext;

// Custom hook to use the Auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
